import isNil from 'lodash/isNil';
import { type ComponentProps, type CSSProperties, type ReactNode } from 'react';

import { Div } from '@ui/components/base/div';
import { Tooltip as TooltipRoot, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/base/tooltip';
import { Typography } from '@ui/components/base/typography';

type TooltipContentProps = ComponentProps<typeof TooltipContent>;

interface TooltipProps {
  content: ReactNode;
  note?: string;
  children: ReactNode;
  side?: TooltipContentProps['side'];
  align?: TooltipContentProps['align'];
  triggerClassName?: string;
  triggerStyle?: CSSProperties;
  contentClassName?: string;
  focusable?: boolean;
  delay?: number;
}

const FOCUSABLE_TAB_INDEX = 0;

const Tooltip = ({
  content,
  note,
  children,
  side,
  align,
  triggerClassName,
  triggerStyle,
  contentClassName,
  focusable = false,
  delay,
}: TooltipProps) => {
  if (isNil(content) || content === '') {
    return <>{children}</>;
  }
  return (
    <TooltipProvider delay={delay}>
      <TooltipRoot>
        <TooltipTrigger
          render={
            <span
              className={triggerClassName}
              style={triggerStyle}
              tabIndex={focusable ? FOCUSABLE_TAB_INDEX : undefined}
            />
          }
        >
          {children}
        </TooltipTrigger>
        <TooltipContent align={align} className={contentClassName} side={side}>
          {isNil(note) ? (
            content
          ) : (
            <Div column data-slot="tooltip-with-note" className="gap-0.5">
              <Typography size="xs">{content}</Typography>
              <Typography size="xs" className="opacity-75">
                {note}
              </Typography>
            </Div>
          )}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

export { Tooltip };
export type { TooltipProps };
